/**
 * Morse timing (docs/MORSE.md §3): text → a list of on/off spans in ms, which
 * the client plays as a tone, a flash or a vibration from the one timeline.
 *
 * **Farnsworth timing.** Each letter is sent at full character speed (`charWpm`,
 * default 20) so it is heard as one sound-shape rather than as dots to count;
 * the *gaps between* letters and words are stretched until the overall pace is
 * `effectiveWpm`. That is the method for learning by ear — a letter learned
 * slowly is learned as a count, and a count tops out around 10 wpm.
 *
 * PARIS standard: one dot = 1200 / wpm ms. Intra-letter gap 1 dot, dash 3 dots.
 * The stretched letter/word gaps follow ARRL's Farnsworth formula: the total
 * delay ta = (60·c − 37.2·s) / (s·c) seconds, of which 3/19 goes to each letter
 * gap and 7/19 to each word gap.
 */
import { MORSE_CODE } from './code.js';
export const DEFAULT_SPEED = { charWpm: 20, effectiveWpm: 10 };
export function gaps({ charWpm, effectiveWpm }) {
    const c = Math.max(5, charWpm);
    const s = Math.min(c, Math.max(2, effectiveWpm));
    const dot = 1200 / c;
    if (s >= c)
        return { dot, letterGap: 3 * dot, wordGap: 7 * dot };
    const ta = ((60 * c - 37.2 * s) / (s * c)) * 1000;
    return { dot, letterGap: (3 * ta) / 19, wordGap: (7 * ta) / 19 };
}
/**
 * The on/off timeline for `text`. Unknown characters are skipped; spaces are
 * word gaps. Starts with an `on` span and never ends with a gap, so the total
 * duration is exactly what a listener hears.
 */
export function timeline(text, speed = DEFAULT_SPEED) {
    const g = gaps(speed);
    const spans = [];
    const words = text.toUpperCase().split(/\s+/).filter(Boolean);
    words.forEach((word, wi) => {
        const letters = [...word].filter((ch) => MORSE_CODE[ch]);
        letters.forEach((ch, li) => {
            [...MORSE_CODE[ch]].forEach((el, ei) => {
                if (ei > 0)
                    spans.push({ on: false, ms: g.dot });
                spans.push({ on: true, ms: el === '.' ? g.dot : 3 * g.dot });
            });
            if (li < letters.length - 1)
                spans.push({ on: false, ms: g.letterGap });
        });
        if (wi < words.length - 1 && letters.length)
            spans.push({ on: false, ms: g.wordGap });
    });
    return spans;
}
export function duration(spans) {
    return spans.reduce((t, s) => t + s.ms, 0);
}
/**
 * Stretch a timeline so no span is shorter than `minDotMs`. A 60 ms buzz is
 * below what a phone motor can render as a distinct pulse — vibration needs a
 * slower send than sound to be felt at all, so it is played scaled up, whole,
 * keeping every ratio between dots, dashes and gaps.
 */
export function atLeast(spans, minDotMs) {
    const shortest = Math.min(...spans.map((s) => s.ms));
    if (!Number.isFinite(shortest) || shortest >= minDotMs)
        return spans;
    const k = minDotMs / shortest;
    return spans.map((s) => ({ on: s.on, ms: Math.round(s.ms * k) }));
}
//# sourceMappingURL=timing.js.map